import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery.ts';
import { THEME_MODES, usePreferences, type ThemeMode } from '../preferences/preferences.store.ts';
import { darkTokens, lightTokens, tokensToCssVariables } from '../tokens/index.ts';
import { densityTokens } from '../tokens/density.ts';
import './global.css';

export type { ThemeMode };
export { THEME_MODES };

type ThemeContextValue = {
  mode: ThemeMode;
  resolved: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STYLE_ID = 'platform-theme-tokens';

const themeCss = [
  tokensToCssVariables(lightTokens),
  tokensToCssVariables({ color: darkTokens.color, shadow: darkTokens.shadow }, ':root[data-theme="dark"]'),
  tokensToCssVariables(densityTokens.spacious),
  ...Object.entries(densityTokens).map(([density, tokens]) =>
    tokensToCssVariables(tokens, `:root[data-density="${density}"]`),
  ),
].join(' ');

const ensureStyleElement = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = themeCss;
  document.head.append(style);
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const mode = usePreferences((state) => state.theme);
  const setMode = usePreferences((state) => state.setTheme);
  const density = usePreferences((state) => state.density);
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const resolved = mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    ensureStyleElement();
    document.documentElement.dataset['theme'] = resolved;
  }, [resolved]);

  useEffect(() => {
    document.documentElement.dataset['density'] = density;
  }, [density]);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme requires ThemeProvider');
  return context;
};
